import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { coverageAPI, vehiclesAPI } from '../services/api';

function CoverageHistoryPage() {
  const [coverageHistory, setCoverageHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [historyRes, vehiclesRes] = await Promise.all([
        coverageAPI.getHistory(),
        vehiclesAPI.getAll(),
      ]);
      setCoverageHistory(historyRes.data);
      setVehicles(vehiclesRes.data);
      setError(null);
    } catch (err) {
      console.error('Error loading coverage history:', err);
      setError('Failed to load coverage history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'En cours';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (days) => {
    if (days === 0) return '< 1 jour';
    if (days === 1) return '1 jour';
    if (days < 30) return `${days} jours`;
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    if (remainingDays === 0) return `${months} mois`;
    return `${months} mois ${remainingDays} jours`;
  };

  const filteredHistory = coverageHistory.filter((record) => {
    const matchesSearch =
      (record.vehicle_registration &&
        record.vehicle_registration.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (record.vehicle_name &&
        record.vehicle_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      record.device_imei.includes(searchTerm) ||
      (record.contract_number &&
        record.contract_number.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesVehicle =
      vehicleFilter === 'all' ||
      record.vehicle_name === vehicleFilter ||
      record.vehicle_registration === vehicleFilter;

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && record.is_active) ||
      (statusFilter === 'completed' && !record.is_active);

    return matchesSearch && matchesVehicle && matchesStatus;
  });

  const paginatedHistory = filteredHistory.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <HistoryIcon color="primary" fontSize="large" />
        <Typography variant="h4" component="h1">
          Historique de Couverture
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          sx={{ flex: 1, minWidth: 300 }}
          placeholder="Rechercher par véhicule, contrat ou IMEI..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Véhicule</InputLabel>
          <Select
            value={vehicleFilter}
            label="Véhicule"
            onChange={(e) => setVehicleFilter(e.target.value)}
          >
            <MenuItem value="all">Tous les véhicules</MenuItem>
            {vehicles.map((vehicle) => (
              <MenuItem key={vehicle.id} value={vehicle.registration_number || vehicle.name}>
                {vehicle.registration_number || vehicle.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            label="Statut"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="active">En cours</MenuItem>
            <MenuItem value="completed">Terminé</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Contrat</strong></TableCell>
              <TableCell><strong>Véhicule</strong></TableCell>
              <TableCell><strong>IMEI Appareil</strong></TableCell>
              <TableCell><strong>Début Couverture</strong></TableCell>
              <TableCell><strong>Fin Couverture</strong></TableCell>
              <TableCell><strong>Durée</strong></TableCell>
              <TableCell><strong>Facturable</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHistory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucun historique de couverture trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedHistory.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <strong>{record.contract_number || '-'}</strong>
                  </TableCell>
                  <TableCell>
                    {record.vehicle_registration || record.vehicle_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.device_imei}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(record.coverage_start)}</TableCell>
                  <TableCell>{formatDate(record.coverage_end)}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatDuration(record.duration)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {record.is_billable ? (
                      <Chip label="Oui" color="success" size="small" />
                    ) : (
                      <Chip label="Non" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {record.is_active ? (
                      <Chip
                        icon={<ActiveIcon />}
                        label="En cours"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<InactiveIcon />}
                        label="Terminé"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredHistory.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Chip
          label={`Total: ${coverageHistory.length}`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`En cours: ${coverageHistory.filter((r) => r.is_active).length}`}
          color="success"
        />
        <Chip
          label={`Terminé: ${coverageHistory.filter((r) => !r.is_active).length}`}
          color="default"
        />
      </Box>
    </Container>
  );
}

export default CoverageHistoryPage;
